import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  motion, 
  AnimatePresence,
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from 'framer-motion';
import { 
  PanelLeft, PanelLeftClose, Plus, Search, Clock, Video as VideoIcon,
  MoreHorizontal, Download, Maximize2, Copy, Mic, Sparkles, 
  Wand2, Zap, Grid3x3, Trash2, Pencil, Paperclip, Send, X, Play
} from 'lucide-react';
import { VideoChatInterface } from './VideoChatInterface';
import { VideoGalleryView } from './VideoGalleryView';
import keiAIVideoService, { pollVideoTaskStatus } from '../services/keiAIVideoService';
import * as contentService from '../services/contentService';
import { pollJob } from '../services/contentService';
import { HistorySidebar } from './HistorySidebar';
import { useAuth } from '../contexts/AuthContext';

// MovingBorder Component
const MovingBorder = ({
  children,
  duration = 2000,
  rx,
  ry,
  ...otherProps
}) => {
  const pathRef = useRef(null);
  const progress = useMotionValue(0);

  useAnimationFrame((time) => {
    const length = pathRef.current?.getTotalLength();
    if (length) {
      const pxPerMillisecond = length / duration;
      progress.set((time * pxPerMillisecond) % length);
    }
  });

  const x = useTransform(
    progress,
    (val) => pathRef.current?.getPointAtLength(val).x
  );
  const y = useTransform(
    progress,
    (val) => pathRef.current?.getPointAtLength(val).y
  );

  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        className="absolute h-full w-full"
        width="100%"
        height="100%"
        {...otherProps}
      >
        <rect
          fill="none"
          width="100%"
          height="100%"
          rx={rx}
          ry={ry}
          ref={pathRef}
        />
      </svg>
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          display: "inline-block",
          transform,
        }}
      >
        {children}
      </motion.div>
    </>
  );
};

const STYLES = [
  { id: 'cinematic', label: 'Cinematic', emoji: '🎬' },
  { id: 'realistic', label: 'Realistic', emoji: '📹' },
  { id: 'animated', label: 'Animated', emoji: '✨' },
  { id: 'documentary', label: 'Documentary', emoji: '📽️' },
  { id: 'artistic', label: 'Artistic', emoji: '🎨' },
  { id: 'timelapse', label: 'Timelapse', emoji: '⏱️' },
  { id: 'slow-motion', label: 'Slow Motion', emoji: '🐌' },
  { id: 'drone', label: 'Drone Shot', emoji: '🚁' },
];

const ASPECT_RATIOS = [
  { id: '16:9', label: 'Landscape', display: '16:9' },
  { id: '9:16', label: 'Portrait', display: '9:16' },
  { id: '1:1', label: 'Square', display: '1:1' },
  { id: '21:9', label: 'Ultrawide', display: '21:9' },
];

// Model-specific durations will be set based on selected model

// Model metadata mapping for UI enrichment
const MODEL_METADATA = {
  'kling-3.0': {
    logo: '/images/logos/meta.svg',
    description: 'Advanced video generation with multi-shot and element references.',
    color: '#8B5CF6'
  },
  'kling-2.6': {
    logo: '/images/logos/meta.svg',
    description: 'High-quality video generation with strong motion consistency.',
    color: '#8B5CF6'
  },
  'grok-imagine': {
    logo: '/images/logos/xai.svg',
    description: 'xAI\'s multimodal video generation with coherent motion and synchronized audio.',
    color: '#14B8A6'
  },
  'seedance-2.0': {
    logo: '/bytedance.svg',
    description: 'ByteDance\'s flagship video generation model for realistic movement.',
    color: '#06B6D4'
  },
  'seedance-2.0-fast': {
    logo: '/bytedance.svg',
    description: 'High-speed video generation without compromising quality.',
    color: '#06B6D4'
  },
  'sora2-pro': {
    logo: '/images/logos/openai.svg',
    description: 'Premium Sora2 variant with enhanced detail and longer durations.',
    color: '#10B981'
  },
  'sora2': {
    logo: '/images/logos/openai.svg',
    description: 'Balanced Sora2 model for diverse video creation needs.',
    color: '#10B981'
  },
  'happyhorse': {
    logo: '/images/logos/google.svg',
    description: 'Playful and creative video generation for stylized content.',
    color: '#F59E0B'
  },
  'veo-3.1': {
    logo: '/images/logos/google.svg',
    description: 'Google\'s latest video generation model with advanced motion control.',
    color: '#4285F4'
  }
};

const DEFAULT_VIDEO_MODELS = [
  { id: 'kling-3.0',         name: 'Kling 3.0',         provider: 'Kling',      logo: '/images/logos/meta.svg',  durations: [5, 10, 15], aspectRatios: ['16:9', '9:16', '1:1'] },
  { id: 'kling-2.6',         name: 'Kling 2.6',         provider: 'Kling',      logo: '/images/logos/meta.svg',  durations: [5, 10],     aspectRatios: ['16:9', '9:16', '1:1'] },
  { id: 'grok-imagine',      name: 'Grok Imagine',      provider: 'xAI',        logo: '/images/logos/xai.svg',   durations: [6, 10],     aspectRatios: ['16:9', '9:16', '1:1', '2:3', '3:2'] },
  { id: 'seedance-2.0',      name: 'Seedance 2.0',      provider: 'ByteDance',  logo: '/bytedance.svg',          durations: [8, 12],     aspectRatios: ['16:9', '9:16', '1:1'] },
  { id: 'seedance-2.0-fast', name: 'Seedance 2.0 Fast', provider: 'ByteDance',  logo: '/bytedance.svg',          durations: [5, 8],      aspectRatios: ['16:9', '9:16', '1:1'] },
  { id: 'sora2-pro',         name: 'Sora2 Pro',         provider: 'Sora2',      logo: '/images/logos/openai.svg',durations: [10, 15, 20],aspectRatios: ['16:9', '9:16'] },
  { id: 'sora2',             name: 'Sora2',             provider: 'Sora2',      logo: '/images/logos/openai.svg',durations: [5, 10],     aspectRatios: ['16:9', '9:16', '1:1'] },
  { id: 'happyhorse',        name: 'HappyHorse',        provider: 'HappyHorse', logo: '/images/logos/google.svg',durations: [5, 10],     aspectRatios: ['16:9', '9:16', '1:1'] },
  { id: 'veo-3.1',           name: 'Veo 3.1',           provider: 'Google',     logo: '/images/logos/google.svg',durations: [5, 10],     aspectRatios: ['16:9', '9:16'] },
].map(m => ({
  ...m,
  description: MODEL_METADATA[m.id]?.description || '',
  color: MODEL_METADATA[m.id]?.color || '#7C3AED',
  functions: ['Text to Video', 'Image to Video'],
  speed: m.name.includes('Fast') ? 5 : 4,
  quality: m.name.includes('Pro') ? 5 : 4,
}));

export function VideoGenerationWorkspace() {
  const location = useLocation();
  const navigate = useNavigate();
  const { initialPrompt, selectedModel: initialModel, aspectRatio: initialRatio } = location.state || {};
  const { userProfile } = useAuth();
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeHistoryId, setActiveHistoryId] = useState(null);
  
  // View state - 'chat' or 'gallery'
  const [currentView, setCurrentView] = useState('chat');
  
  // Conversation state
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Generation state
  const [videoModels, setVideoModels] = useState(DEFAULT_VIDEO_MODELS);
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [selectedRatio, setSelectedRatio] = useState(initialRatio || '16:9');
  const [selectedDuration, setSelectedDuration] = useState('5');
  const [selectedModelId, setSelectedModelId] = useState(initialModel || 'grok-imagine');
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  
  // Fetch real models from registry
  useEffect(() => {
    const fetchRegistryModels = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/models/function/video`);
        const data = await response.json();
        if (data.success && data.models?.length > 0) {
          const mapped = data.models.map(m => ({
            id: m.model_id,
            name: m.display_name || m.name,
            provider: m.provider,
            functions: m.functions || ['Text to Video'],
            durations: m.durations || [5, 10],
            aspectRatios: m.aspect_ratios || ['16:9', '9:16', '1:1'],
            ...(MODEL_METADATA[m.model_id] || {})
          }));
          setVideoModels(mapped);
          
          // Auto-select first model if current is invalid
          if (!mapped.find(m => m.id === selectedModelId)) {
            setSelectedModelId(mapped[0].id);
          }
        }
      } catch (err) {
        console.warn('Failed to fetch video models from registry, using fallbacks');
      }
    };
    fetchRegistryModels();
  }, []);
  
  // Gallery state
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [error, setError] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  
  // Grid overlay toggle
  const [showGrid, setShowGrid] = useState(true);
  
  // Get current model configuration
  const selectedModel = videoModels.find(m => m.id === selectedModelId) || videoModels[0];
  
  // Update duration and aspect ratio when model changes
  useEffect(() => {
    if (selectedModel) {
      // Set default duration to first available option for the model
      if (selectedModel.durations && !selectedModel.durations.includes(parseInt(selectedDuration))) {
        setSelectedDuration(selectedModel.durations[0].toString());
      }
      
      // Set default aspect ratio to first available option for the model
      if (selectedModel.aspectRatios && !selectedModel.aspectRatios.includes(selectedRatio)) {
        setSelectedRatio(selectedModel.aspectRatios[0]);
      }
    }
  }, [selectedModelId]);
  
  // Close model dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showModelDropdown && !event.target.closest('.model-dropdown-container')) {
        setShowModelDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModelDropdown]);

  const createNewConversation = () => {
    const newConversation = {
      id: Date.now().toString(),
      title: 'New Generation',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setConversations(prev => [newConversation, ...prev]);
    setActiveConversationId(newConversation.id);
    setMessages([]);
    setPrompt('');
    setAttachmentFile(null);
    setCurrentView('chat');
  };

  const loadConversation = (conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setActiveConversationId(conversationId);
      setMessages(conversation.messages);
      setCurrentView('chat');
    }
  };

  // Load video history from D1 on mount (fallback to localStorage for guests)
  useEffect(() => {
    const loadHistory = async () => {
      if (userProfile?.id) {
        const videos = await contentService.getVideos(userProfile.id);
        if (videos.length > 0) {
          const convs = videos.slice(0, 20).map(v => ({
            id: v.id,
            title: v.prompt?.substring(0, 50) || 'Video',
            messages: [],
            createdAt: new Date(v.created_at * 1000).toISOString(),
            updatedAt: new Date(v.created_at * 1000).toISOString(),
          }));
          setConversations(convs);
          return;
        }
      }
      // Guest fallback
      const savedConversations = localStorage.getItem('videoConversations');
      if (savedConversations) {
        try {
          const parsed = JSON.parse(savedConversations);
          setConversations(parsed);
          if (parsed.length > 0) {
            setActiveConversationId(parsed[0].id);
            setMessages(parsed[0].messages || []);
          }
        } catch (error) {
          localStorage.removeItem('videoConversations');
        }
      }
    };
    loadHistory();
  }, [userProfile?.id]);

  // Save conversations to localStorage for guest users only
  useEffect(() => {
    if (!userProfile?.id && conversations.length > 0) {
      localStorage.setItem('videoConversations', JSON.stringify(conversations));
    }
  }, [conversations, userProfile?.id]);

  // Update conversation title based on first prompt
  const updateConversationTitle = (conversationId, firstPrompt) => {
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId && conv.title === 'New Generation') {
        return {
          ...conv,
          title: firstPrompt.substring(0, 50) + (firstPrompt.length > 50 ? '...' : ''),
          updatedAt: new Date().toISOString()
        };
      }
      return conv;
    }));
  };

  const toggleStyle = (styleId) => {
    setSelectedStyles(prev => 
      prev.includes(styleId) 
        ? prev.filter(s => s !== styleId)
        : [...prev, styleId]
    );
  };

  // Auto-trigger generation when arriving from landing page with initialPrompt
  const hasAutoGenerated = useRef(false);
  useEffect(() => {
    if (initialPrompt && !hasAutoGenerated.current && !isGenerating && prompt.trim()) {
      hasAutoGenerated.current = true;
      // Clear history state so page refresh doesn't re-trigger generation
      window.history.replaceState({}, '', window.location.href);
      const timer = setTimeout(() => handleGenerate(), 300);
      return () => clearTimeout(timer);
    }
  }, [initialPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAttachmentClick = () => {
    document.getElementById('video-attachment-input').click();
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
      setAttachmentFile(file);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim() && !attachmentFile) return;

    // Create new conversation inline if none exists (don't bail out!)
    let convId = activeConversationId;
    if (!convId) {
      const newConversation = {
        id: Date.now().toString(),
        title: 'New Generation',
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setConversations(prev => [newConversation, ...prev]);
      setActiveConversationId(newConversation.id);
      convId = newConversation.id;
    }

    // Update conversation title if it's the first message
    if (messages.length === 0) {
      updateConversationTitle(convId, prompt);
    }

    try {
      setIsGenerating(true);
      setError(null);

      // Add user message
      const userMessage = {
        role: 'user',
        content: prompt,
        attachment: attachmentFile ? URL.createObjectURL(attachmentFile) : null,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, userMessage]);
      const currentPrompt = prompt;
      setPrompt('');
      
      // Upload attachment if present
      let imageInput = null;
      let videoInput = null;
      
      if (attachmentFile) {
        console.log('📤 Uploading reference media...');
        const uploadedUrl = await keiAIVideoService.uploadReferenceMedia(attachmentFile);
        
        if (attachmentFile.type.startsWith('image/')) {
          imageInput = uploadedUrl;
        } else if (attachmentFile.type.startsWith('video/')) {
          videoInput = uploadedUrl;
        }
        
        console.log('✅ Reference media uploaded:', uploadedUrl);
      }
      
      setAttachmentFile(null);

      // Submit job - keiAIVideoService registers it in D1 and passes webhook URL
      // It returns { success, taskId } immediately (no internal polling)
      console.log('[VideoGen] Submitting job to', selectedModelId, '...');
      const taskResponse = await keiAIVideoService.generateVideoSubmit(
        selectedModelId, currentPrompt,
        { imageInput, videoInput, aspectRatio: selectedRatio, duration: parseInt(selectedDuration) },
        userProfile?.id
      );
      const taskId = taskResponse?.taskId;
      if (!taskId) throw new Error('No taskId returned from generation API');
      console.log('[VideoGen] Job submitted, taskId:', taskId);

      // Strategy: try Worker-based job polling first (D1 webhook-updated jobs).
      // Fall back to DIRECT Kei.ai polling if:
      //   a) job not found in D1 (null) 3 times, OR
      //   b) job stuck in pending/processing for 8+ attempts (~40s) - webhook may have been missed
      let videoUrl = null;

      let workerNullCount    = 0;
      let workerStuckCount   = 0;        // increments each poll that isn't completed/failed
      const MAX_WORKER_NULLS = 3;
      const MAX_STUCK_POLLS  = 8;        // ~40 seconds before falling to direct polling
      let workerDone  = false;

      if (userProfile?.id) {
        console.log('[VideoGen] Trying Worker job polling for userId:', userProfile.id);
        try {
          const job = await pollJob(userProfile.id, taskId, {
            intervalMs: 5000,
            maxAttempts: 72,
            onProgress: (j, attempt) => {
              const st = j?.status;
              console.log(`[VideoGen] Worker poll attempt ${attempt}, status:`, st);

              if (j === null || j === undefined) {
                workerNullCount++;
              } else if (st !== 'completed' && st !== 'failed') {
                workerStuckCount++;
              }

              // Bail → direct Kei.AI if: job missing 3x OR stuck non-terminal 8x
              if (workerNullCount >= MAX_WORKER_NULLS || workerStuckCount >= MAX_STUCK_POLLS) {
                console.log(`[VideoGen] Falling back to direct Kei.ai poll (nulls=${workerNullCount} stuck=${workerStuckCount})`);
                throw new Error('__FALLBACK__');
              }
            },
          });
          videoUrl = job?.resultUrl || job?.result_url || job?.videoUrl || job?.video_url;
          workerDone = true;
          console.log('[VideoGen] Worker polling succeeded, videoUrl:', videoUrl);
        } catch (err) {
          if (err.message === '__FALLBACK__' || err.message?.includes('timed out')) {
            console.log('[VideoGen] Falling back to direct Kei.ai polling for taskId:', taskId);
          } else {
            console.warn('[VideoGen] Worker polling error:', err.message);
          }
        }
      }

      // Direct Kei.ai polling - runs if Worker fallback triggered OR worker not available
      if (!workerDone || !videoUrl) {
        console.log('[VideoGen] Direct Kei.ai task polling for taskId:', taskId);
        const result = await pollVideoTaskStatus(taskId, 72, 5000);
        videoUrl = result?.videoUrl;
        console.log('[VideoGen] Direct polling result:', result?.status, videoUrl);
      }


      if (!videoUrl) throw new Error('Generation completed but no video URL returned');

      // Add assistant message
      const assistantMessage = {
        role: 'assistant',
        video: videoUrl,
        thumbnailUrl: null,
        prompt: userMessage.content,
        model: selectedModelId,
        aspectRatio: selectedRatio,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);

      setConversations(prev => prev.map(conv => {
        if (conv.id === convId) {
          return { ...conv, messages: [...conv.messages, userMessage, assistantMessage], updatedAt: new Date().toISOString() };
        }
        return conv;
      }));

      setGeneratedVideos(prev => [{ url: videoUrl, prompt: userMessage.content, conversationId: convId }, ...prev]);

      // Persist to D1 + R2 so it appears in Gallery - fire and forget
      if (userProfile?.id) {
        contentService.saveVideo(userProfile.id, {
          prompt: userMessage.content,
          model: selectedModelId,
          url: videoUrl,
          duration: parseInt(selectedDuration),
          metadata: { aspectRatio: selectedRatio }
        }).catch(err => console.warn('[VideoGen] saveVideo error:', err));
      }


    } catch (err) {
      console.error('Error generating video:', err);
      setError(err.message || 'Failed to generate video');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = (videoUrl) => {
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = `video-${Date.now()}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* SIDEBAR */}
      <motion.div
        initial={false}
        animate={{ 
          width: sidebarOpen ? '320px' : '0px',
          opacity: sidebarOpen ? 1 : 0
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: '100%',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRight: '1px solid rgba(148, 163, 184, 0.1)',
          boxShadow: '4px 0 24px rgba(15, 23, 42, 0.08)',
          zIndex: 50,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {sidebarOpen && (
          <>
            {/* Logo & Toggle */}
            <div
              className="flex items-center justify-between px-4 pt-5 pb-4"
              style={{ borderBottom: '1px solid #f3f4f6' }}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0"
                  style={{ background: '#ede9fe', border: '1px solid #ddd6fe' }}
                >
                  <img src="/DAGGPT-01.jpg" alt="" className="w-7 h-7 object-contain" />
                </div>
                <div>
                  <span className="text-[14px] font-bold text-slate-900 tracking-tight">DAGGPT</span>
                  <span
                    className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: '#ede9fe', color: '#7c3aed' }}
                  >
                    BETA
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
              >
                <PanelLeftClose className="w-4 h-4" />
              </button>
            </div>

            {/* New Generation Button */}
            <div className="px-3 pb-2" style={{ marginTop: '80px' }}>
              <button
                onClick={createNewConversation}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                  boxShadow: '0 2px 12px rgba(124,58,237,0.25)',
                }}
              >
                <Plus className="w-4 h-4" />
                New Generation
              </button>
            </div>

            {/* Gallery Button */}
            <div className="px-3 pb-3">
              <button
                onClick={() => setCurrentView('gallery')}
                className="w-full flex items-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all hover:bg-slate-100 active:scale-[0.98]"
                style={{
                  background: currentView === 'gallery' ? '#f1f5f9' : 'transparent',
                  color: currentView === 'gallery' ? '#7c3aed' : '#64748b',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="10" y="1" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="1" y="10" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <rect x="10" y="10" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                </svg>
                Gallery
              </button>
            </div>

            {/* Controls in Sidebar */}
            <div
              className="flex-1 overflow-y-auto px-3 pb-2"
              style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}
            >
              {/* AI Model */}
              <div className="mb-4">
                <label className="text-xs font-bold mb-2 block text-slate-900">AI Model</label>
                
                {/* Custom Model Selector with Logos */}
                <div className="relative model-dropdown-container">
                  <button
                    onClick={() => setShowModelDropdown(!showModelDropdown)}
                    className="w-full px-3 py-2.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-between"
                    style={{
                      background: '#ffffff',
                      border: '2px solid #7c3aed',
                      color: '#0f172a',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {selectedModel?.logo && (
                        <img 
                          src={selectedModel.logo} 
                          alt={selectedModel.provider}
                          className="w-4 h-4 object-contain"
                        />
                      )}
                      <span>{selectedModel?.name}</span>
                    </div>
                    <svg 
                      className={`w-4 h-4 transition-transform ${showModelDropdown ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showModelDropdown && (
                    <div 
                      className="absolute z-50 w-full mt-1 rounded-lg shadow-xl overflow-hidden"
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        maxHeight: '300px',
                        overflowY: 'auto',
                      }}
                    >
                      {videoModels.map(model => (
                        <button
                          key={model.id}
                          onClick={() => {
                            setSelectedModelId(model.id);
                            setShowModelDropdown(false);
                          }}
                          className="w-full px-3 py-2.5 text-xs font-semibold transition-all flex items-center gap-2 hover:bg-purple-50"
                          style={{
                            background: selectedModelId === model.id ? 'rgba(124, 58, 237, 0.1)' : 'transparent',
                            color: '#0f172a',
                            textAlign: 'left',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          {model.logo && (
                            <img 
                              src={model.logo} 
                              alt={model.provider}
                              className="w-4 h-4 object-contain flex-shrink-0"
                            />
                          )}
                          <span className="flex-1">{model.name}</span>
                          {model.isBeta && (
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#d97706' }}>BETA</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="mt-2 text-[10px] text-slate-600">
                  {selectedModel?.description}
                </div>
                {selectedModel?.isBeta && selectedModel?.betaNote && (
                  <div className="mt-2 px-2.5 py-2 rounded-lg text-[10px] font-medium flex items-start gap-1.5" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                    <span>⚠️</span>
                    <span>{selectedModel.betaNote}</span>
                  </div>
                )}
              </div>

              {/* Style Presets */}
              <div className="mb-4">
                <label className="text-xs font-bold mb-2 block text-slate-900">Style Presets</label>
                <div className="flex flex-wrap gap-1.5">
                  {STYLES.map(style => {
                    const isSelected = selectedStyles.includes(style.id);
                    return (
                      <button
                        key={style.id}
                        onClick={() => toggleStyle(style.id)}
                        className="px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all"
                        style={{
                          background: isSelected ? 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' : '#ffffff',
                          color: isSelected ? '#ffffff' : '#475569',
                          border: isSelected ? 'none' : '1px solid #e2e8f0',
                          boxShadow: isSelected ? '0 2px 8px rgba(124, 58, 237, 0.3)' : 'none',
                        }}
                      >
                        {style.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Aspect Ratio - Model Specific */}
              <div className="mb-4">
                <label className="text-xs font-bold mb-2 block text-slate-900">Aspect Ratio</label>
                <div className="grid grid-cols-2 gap-2">
                  {selectedModel?.aspectRatios?.map(ratioValue => {
                    const isSelected = selectedRatio === ratioValue;
                    const ratioInfo = ASPECT_RATIOS.find(r => r.id === ratioValue) || { id: ratioValue, label: ratioValue, display: ratioValue };
                    return (
                      <button
                        key={ratioValue}
                        onClick={() => setSelectedRatio(ratioValue)}
                        className="p-3 rounded-lg text-xs font-semibold transition-all"
                        style={{
                          background: isSelected ? 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' : '#ffffff',
                          color: isSelected ? '#ffffff' : '#475569',
                          border: isSelected ? 'none' : '1px solid #e2e8f0',
                          boxShadow: isSelected ? '0 2px 8px rgba(124, 58, 237, 0.3)' : 'none',
                        }}
                      >
                        <div className="text-[10px] opacity-70 mb-1">{ratioInfo.label}</div>
                        <div className="font-bold">{ratioInfo.display}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Duration - Model Specific */}
              <div className="mb-4">
                <label className="text-xs font-bold mb-2 block text-slate-900">Duration</label>
                <div className="grid grid-cols-2 gap-2">
                  {selectedModel?.durations?.map(durationValue => {
                    const isSelected = selectedDuration === durationValue.toString();
                    return (
                      <button
                        key={durationValue}
                        onClick={() => setSelectedDuration(durationValue.toString())}
                        className="p-2.5 rounded-lg text-xs font-semibold transition-all"
                        style={{
                          background: isSelected ? 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' : '#ffffff',
                          color: isSelected ? '#ffffff' : '#475569',
                          border: isSelected ? 'none' : '1px solid #e2e8f0',
                          boxShadow: isSelected ? '0 2px 8px rgba(124, 58, 237, 0.3)' : 'none',
                        }}
                      >
                        {durationValue}s
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* History Section */}
            <div className="px-3 pb-2">
              <div className="text-xs font-bold mb-2 text-slate-900">History</div>
              <div className="space-y-1 max-h-48 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                {conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => loadConversation(conv.id)}
                    className="w-full text-left px-3 py-2 rounded-lg text-xs transition-all hover:bg-slate-100"
                    style={{
                      background: activeConversationId === conv.id ? '#f1f5f9' : 'transparent',
                      color: activeConversationId === conv.id ? '#7c3aed' : '#64748b',
                      fontWeight: activeConversationId === conv.id ? '600' : '500',
                    }}
                  >
                    <div className="truncate">{conv.title}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {new Date(conv.updatedAt).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Bottom User Profile */}
            <div
              className="p-3"
              style={{ borderTop: '1px solid #f3f4f6' }}
            >
              <div
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all hover:bg-slate-100"
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
                  style={{
                    background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                  }}
                >
                  U
                </div>
                <div className="flex-1">
                  <div className="text-xs font-semibold text-slate-900">User</div>
                  <div className="text-[10px] text-slate-500">Premium Plan</div>
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* MAIN CONTENT AREA */}
      <div style={{
        marginLeft: sidebarOpen ? '320px' : '0px',
        height: '100%',
        transition: 'margin-left 0.3s ease-in-out',
        position: 'relative',
      }}>
        {/* Top Bar */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '50px',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          gap: '12px',
          zIndex: 40,
        }}>
          {!sidebarOpen && (
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <PanelLeft size={20} color="#64748b" />
            </button>
          )}
          
          <div className="flex-1 flex items-center gap-3">
            <h1 className="text-sm font-bold text-slate-900">
              {currentView === 'gallery' ? 'Video Gallery' : 'Video Generation'}
            </h1>
          </div>
        </div>

        {/* CENTER CANVAS - Chat Interface or Gallery View */}
        <div style={{
          position: 'absolute',
          top: '50px',
          left: 0,
          right: 0,
          bottom: '180px',
          overflow: 'hidden',
        }}>
          {currentView === 'chat' ? (
            <VideoChatInterface
              messages={messages}
              isGenerating={isGenerating}
              onDownload={handleDownload}
              selectedRatio={selectedRatio}
            />
          ) : (
            <VideoGalleryView
              videos={generatedVideos}
              onDownload={handleDownload}
              onExpand={setExpandedVideo}
            />
          )}
        </div>

        {/* BOTTOM INPUT AREA */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '180px',
          padding: '20px 60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ 
            width: '100%', 
            maxWidth: '900px',
            position: 'relative',
          }}>
            <MovingBorder duration={3000} rx="24" ry="24">
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '24px',
                  padding: '2px',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #7c3aed 100%)',
                  opacity: 0.6,
                }}
              />
            </MovingBorder>
            
            {/* Content */}
            <div className="rounded-2xl p-4" style={{
              background: 'rgba(255, 255, 255, 0.98)',
              border: '1px solid #e5e7eb',
              boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12)',
              backdropFilter: 'blur(20px)',
            }}>
            
            <div className="flex items-end gap-3">
              {/* Attachment Preview - Above Paperclip Icon */}
              <div className="flex flex-col items-center gap-2">
                {attachmentFile && (
                  <div className="relative">
                    {attachmentFile.type.startsWith('image/') ? (
                      <img 
                        src={URL.createObjectURL(attachmentFile)} 
                        alt="Attachment preview"
                        className="rounded-lg object-cover"
                        style={{
                          width: '42px',
                          height: '42px',
                          border: '2px solid rgba(124, 58, 237, 0.3)',
                        }}
                      />
                    ) : (
                      <div className="rounded-lg flex items-center justify-center"
                        style={{
                          width: '42px',
                          height: '42px',
                          border: '2px solid rgba(124, 58, 237, 0.3)',
                          background: 'rgba(124, 58, 237, 0.1)',
                        }}
                      >
                        <VideoIcon size={20} color="#7c3aed" />
                      </div>
                    )}
                    <button
                      onClick={() => setAttachmentFile(null)}
                      className="absolute -top-1.5 -right-1.5 rounded-full bg-red-500 hover:bg-red-600 transition-colors shadow-lg"
                      style={{
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0',
                      }}
                    >
                      <X size={10} color="#ffffff" />
                    </button>
                  </div>
                )}
                {/* Attachment icon */}
                <input
                  type="file"
                  id="video-attachment-input"
                  accept="image/*,video/*"
                  onChange={handleAttachmentChange}
                  style={{ display: 'none' }}
                />
                <button 
                  onClick={handleAttachmentClick}
                  className="p-2.5 rounded-lg transition-all flex-shrink-0"
                  style={{
                    background: attachmentFile ? 'rgba(124, 58, 237, 0.1)' : '#f8fafc',
                    border: `1px solid ${attachmentFile ? '#7c3aed' : '#e2e8f0'}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(124, 58, 237, 0.1)';
                    e.currentTarget.style.borderColor = '#7c3aed';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = attachmentFile ? 'rgba(124, 58, 237, 0.1)' : '#f8fafc';
                    e.currentTarget.style.borderColor = attachmentFile ? '#7c3aed' : '#e2e8f0';
                  }}
                >
                  <Paperclip size={18} color={attachmentFile ? '#7c3aed' : '#64748b'} />
                </button>
              </div>

              {/* Textarea */}
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={attachmentFile ? "Describe the video transformation..." : "Describe your video vision..."}
                className="flex-1 px-4 py-3 rounded-xl text-sm resize-none transition-all hide-scrollbar"
                rows={1}
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#0f172a',
                  outline: 'none',
                  maxHeight: '120px',
                  minHeight: '44px',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#7c3aed';
                  e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleGenerate();
                  }
                }}
              />

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={isGenerating || (!prompt.trim() && !attachmentFile)}
                className="p-3 rounded-xl transition-all flex-shrink-0"
                style={{
                  background: isGenerating || (!prompt.trim() && !attachmentFile)
                    ? '#e2e8f0'
                    : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                  cursor: isGenerating || (!prompt.trim() && !attachmentFile) ? 'not-allowed' : 'pointer',
                  opacity: isGenerating || (!prompt.trim() && !attachmentFile) ? 0.5 : 1,
                }}
              >
                {isGenerating ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send size={20} color="white" />
                )}
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Video Modal */}
      {expandedVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-8"
          style={{
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
          }}
          onClick={() => setExpandedVideo(null)}
        >
          <div className="relative max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <video
              src={expandedVideo}
              controls
              autoPlay
              className="w-full rounded-2xl"
              style={{
                maxHeight: '80vh',
                objectFit: 'contain',
              }}
            />
            <button
              onClick={() => setExpandedVideo(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            >
              <X size={24} color="white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
